import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Key, Shield, Server, AlertTriangle, CheckCircle } from 'lucide-react';

export default function KeysPage() {
  const { data: keyStatus, isLoading, error } = useQuery({
    queryKey: ['key-status'],
    queryFn: api.getKeyStatus,
    refetchInterval: 60000, // Refresh every minute
  });

  const getStatusColor = (daysRemaining: number, isHealthy?: boolean) => {
    if (isHealthy === false) return 'bg-red-100 text-destructive';
    if (daysRemaining <= 0) return 'bg-red-100 text-destructive';
    if (daysRemaining <= 30) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusIcon = (daysRemaining: number, isHealthy?: boolean) => {
    if (isHealthy === false) return <AlertTriangle className="h-5 w-5 text-destructive" />;
    if (daysRemaining <= 0) return <AlertTriangle className="h-5 w-5 text-destructive" />;
    if (daysRemaining <= 30) return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    return <CheckCircle className="h-5 w-5 text-emerald-500" />;
  };

  const getStatusText = (daysRemaining: number, isHealthy?: boolean) => {
    if (isHealthy === false) return 'Unhealthy';
    if (daysRemaining <= 0) return 'Expired';
    if (daysRemaining <= 30) return 'Expiring Soon';
    return 'Healthy';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Key Management</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-muted rounded w-32"></div>
                <div className="h-4 bg-muted rounded w-24"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-20 mb-2"></div>
                <div className="h-4 bg-muted rounded w-16"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Key Management</h1>
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <p className="text-muted-foreground">Failed to load key status</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!keyStatus) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Key Management</h1>
        <div className="text-sm text-muted-foreground">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* JWT Token */}
        <Card className="relative">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-lg">
              <Key className="h-5 w-5 mr-2" />
              JWT Token
            </CardTitle>
            <CardDescription>Authentication token status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {keyStatus.jwt_expires_in_days} days
                </div>
                <Badge className={getStatusColor(keyStatus.jwt_expires_in_days)}>
                  {getStatusText(keyStatus.jwt_expires_in_days)}
                </Badge>
              </div>
              <div className="flex-shrink-0">
                {getStatusIcon(keyStatus.jwt_expires_in_days)}
              </div>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              {keyStatus.jwt_expires_in_days <= 0 
                ? 'Token has expired' 
                : keyStatus.jwt_expires_in_days <= 30
                ? 'Token expires soon'
                : 'Token is valid'
              }
            </div>
          </CardContent>
        </Card>

        {/* Client Certificate */}
        <Card className="relative">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-lg">
              <Shield className="h-5 w-5 mr-2" />
              Client Certificate
            </CardTitle>
            <CardDescription>mTLS client certificate status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {keyStatus.client_cert_expires_in_days} days
                </div>
                <Badge className={getStatusColor(keyStatus.client_cert_expires_in_days)}>
                  {getStatusText(keyStatus.client_cert_expires_in_days)}
                </Badge>
              </div>
              <div className="flex-shrink-0">
                {getStatusIcon(keyStatus.client_cert_expires_in_days)}
              </div>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              {keyStatus.client_cert_expires_in_days <= 0 
                ? 'Certificate has expired' 
                : keyStatus.client_cert_expires_in_days <= 30
                ? 'Certificate expires soon'
                : 'Certificate is valid'
              }
            </div>
          </CardContent>
        </Card>

        {/* Vault/HSM */}
        <Card className="relative">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-lg">
              <Server className="h-5 w-5 mr-2" />
              Vault/HSM
            </CardTitle>
            <CardDescription>Hardware security module status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold capitalize">
                  {keyStatus.vault_hsm}
                </div>
                <Badge className={getStatusColor(9999, keyStatus.vault_hsm === 'healthy')}>
                  {keyStatus.vault_hsm === 'healthy' ? 'Healthy' : 'Unhealthy'}
                </Badge>
              </div>
              <div className="flex-shrink-0">
                {keyStatus.vault_hsm === 'healthy' 
                  ? <CheckCircle className="h-5 w-5 text-emerald-500" />
                  : <AlertTriangle className="h-5 w-5 text-destructive" />
                }
              </div>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              {keyStatus.vault_hsm === 'healthy' 
                ? 'All systems operational' 
                : 'Service degraded or unavailable'
              }
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Required Section */}
      {(keyStatus.jwt_expires_in_days <= 30 || 
        keyStatus.client_cert_expires_in_days <= 30 || 
        keyStatus.vault_hsm !== 'healthy') && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-orange-800 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Action Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {keyStatus.jwt_expires_in_days <= 30 && (
                <div className="flex items-start space-x-3">
                  <Key className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-orange-800">JWT Token</p>
                    <p className="text-sm text-orange-700">
                      {keyStatus.jwt_expires_in_days <= 0 
                        ? 'Token has expired and needs immediate renewal'
                        : `Token expires in ${keyStatus.jwt_expires_in_days} days. Consider renewing soon.`
                      }
                    </p>
                  </div>
                </div>
              )}
              
              {keyStatus.client_cert_expires_in_days <= 30 && (
                <div className="flex items-start space-x-3">
                  <Shield className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-orange-800">Client Certificate</p>
                    <p className="text-sm text-orange-700">
                      {keyStatus.client_cert_expires_in_days <= 0 
                        ? 'Certificate has expired and needs immediate renewal'
                        : `Certificate expires in ${keyStatus.client_cert_expires_in_days} days. Consider renewing soon.`
                      }
                    </p>
                  </div>
                </div>
              )}
              
              {keyStatus.vault_hsm !== 'healthy' && (
                <div className="flex items-start space-x-3">
                  <Server className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-orange-800">Vault/HSM</p>
                    <p className="text-sm text-orange-700">
                      Hardware security module is reporting an unhealthy status. Check system logs and contact support if needed.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
